import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Scale, Mail, Lock, User, Building2, MapPin, Phone, Check, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { CITIES, CATEGORIES } from '../mock/mock';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

const STEPS = [
  { n: 1, title: 'Dados de acesso' },
  { n: 2, title: 'Sobre o escritório' },
  { n: 3, title: 'Áreas de atuação' },
  { n: 4, title: 'Confirmação' },
];

export default function Register() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: '', password: '',
    firmName: '', leadLawyer: '', oab: '',
    city: new URLSearchParams(window.location.search).get('city') || '', phone: '', whatsapp: '', bio: '',
    categories: [],
    accept: false,
  });
  const { toast } = useToast();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleCat = (slug) => {
    setForm(f => ({
      ...f,
      categories: f.categories.includes(slug) ? f.categories.filter(c => c !== slug) : [...f.categories, slug],
    }));
  };

  const canNext = () => {
    if (step === 1) return form.email && form.password.length >= 6;
    if (step === 2) return form.firmName && form.leadLawyer && form.oab && form.city && form.whatsapp;
    if (step === 3) return form.categories.length > 0;
    if (step === 4) return form.accept;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await register({
        email: form.email,
        password: form.password,
        firmName: form.firmName,
        leadLawyer: form.leadLawyer,
        oab: form.oab,
        city: form.city,
        whatsapp: form.whatsapp,
        phone: '',
        bio: form.bio,
        categories: form.categories,
      });
      toast({ title: 'Cadastro realizado!', description: 'Bem-vindo(a)! Complete seu perfil no painel.' });
      setTimeout(() => navigate('/painel'), 800);
    } catch (err) {
      toast({ title: 'Erro no cadastro', description: err.response?.data?.detail || 'Verifique os dados e tente novamente.', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-[calc(100vh-140px)] bg-white">
      <SEO title="Cadastrar escritório" description="Cadastre seu escritório de advocacia gratuitamente no Ambito Advogados, a plataforma de advogados do Âmbito Jurídico." />
      <div className="aj-wrap max-w-[820px] py-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#edf4f5] border border-[#153e44]/10 rounded-full px-3 py-1 text-xs font-medium text-[#153e44] mb-3">
            <Scale className="w-3 h-3" /> Cadastro para escritórios
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold text-[#153e44]">Cadastre seu escritório</h1>
          <p className="text-[#425a62] mt-2">Conquiste novos clientes com um perfil profissional otimizado para o Google.</p>
        </div>

        {/* Steps indicator */}
        <div className="bg-white rounded-[10px] border border-[#cbd8dc] p-6 mb-6">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.n}>
                <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${step > s.n ? 'bg-[#0a7d3e] text-white' : step === s.n ? 'bg-[#153e44] text-white' : 'bg-[#f2f6f7] text-[#6b858e]'}`}>
                    {step > s.n ? <Check className="w-4 h-4" /> : s.n}
                  </div>
                  <div className={`text-[11px] font-medium text-center hidden sm:block ${step >= s.n ? 'text-[#153e44]' : 'text-[#6b858e]'}`}>{s.title}</div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${step > s.n ? 'bg-[#0a7d3e]' : 'bg-[#e1e9eb]'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-[10px] border border-[#cbd8dc] p-6 md:p-8">
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-[#153e44]">Crie sua conta</h2>
              <div>
                <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">E-mail profissional</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                  <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="contato@escritorio.com.br" className="pl-9 h-11" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Senha (mínimo 6 caracteres)</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                  <Input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} placeholder="••••••••" className="pl-9 h-11" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-[#153e44]">Sobre o escritório</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Nome do escritório</label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                    <Input value={form.firmName} onChange={(e) => set('firmName', e.target.value)} placeholder="Ex: Silva & Souza Advogados" className="pl-9 h-11" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Advogado(a) responsável</label>
                  <div className="relative">
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                    <Input value={form.leadLawyer} onChange={(e) => set('leadLawyer', e.target.value)} placeholder="Dr(a). Nome Completo" className="pl-9 h-11" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Número OAB</label>
                  <Input value={form.oab} onChange={(e) => set('oab', e.target.value)} placeholder="OAB/SP 123456" className="h-11" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Cidade</label>
                  <Select value={form.city} onValueChange={(v) => set('city', v)}>
                    <SelectTrigger className="h-11"><div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#6b858e]" /><SelectValue placeholder="Selecione" /></div></SelectTrigger>
                    <SelectContent>{CITIES.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}/{c.state}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">WhatsApp</label>
                  <div className="relative">
                    <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                    <Input value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} placeholder="(11) 99999-9999" className="pl-9 h-11" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Bio (breve apresentação)</label>
                  <Textarea value={form.bio} onChange={(e) => set('bio', e.target.value)} placeholder="Fale um pouco sobre a atuação do escritório, valores e diferenciais..." rows={4} />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="text-xl font-semibold text-[#153e44] mb-2">Selecione as áreas de atuação</h2>
              <p className="text-sm text-[#425a62] mb-5">Escolha ao menos uma área. Você pode alterar depois.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {CATEGORIES.map(c => {
                  const active = form.categories.includes(c.slug);
                  return (
                    <button key={c.slug} type="button" onClick={() => toggleCat(c.slug)} className={`text-left px-4 py-3 rounded-[7px] border-2 transition-all ${active ? 'border-[#2f5d65] bg-[#edf4f5] text-[#153e44]' : 'border-[#cbd8dc] hover:border-[#bacbd0] text-[#354c54]'}`}>
                      <div className="flex items-center gap-2">
                        {active && <Check className="w-4 h-4 text-[#075a6d]" />}
                        <span className="font-medium text-sm">{c.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="text-xl font-semibold text-[#153e44] mb-2">Revise e confirme</h2>
              <p className="text-sm text-[#425a62] mb-5">Confira os dados antes de finalizar o cadastro.</p>
              <div className="bg-[#f7fafb] rounded-[7px] p-5 space-y-3 text-sm">
                <div className="grid grid-cols-3 gap-2"><span className="text-[#52676e]">E-mail:</span><span className="col-span-2 font-medium text-[#153e44]">{form.email}</span></div>
                <div className="grid grid-cols-3 gap-2"><span className="text-[#52676e]">Escritório:</span><span className="col-span-2 font-medium text-[#153e44]">{form.firmName}</span></div>
                <div className="grid grid-cols-3 gap-2"><span className="text-[#52676e]">Responsável:</span><span className="col-span-2 font-medium text-[#153e44]">{form.leadLawyer}</span></div>
                <div className="grid grid-cols-3 gap-2"><span className="text-[#52676e]">OAB:</span><span className="col-span-2 font-medium text-[#153e44]">{form.oab}</span></div>
                <div className="grid grid-cols-3 gap-2"><span className="text-[#52676e]">Cidade:</span><span className="col-span-2 font-medium text-[#153e44]">{CITIES.find(c => c.slug === form.city)?.name || '—'}</span></div>
                <div className="grid grid-cols-3 gap-2"><span className="text-[#52676e]">WhatsApp:</span><span className="col-span-2 font-medium text-[#153e44]">{form.whatsapp}</span></div>
                <div className="grid grid-cols-3 gap-2"><span className="text-[#52676e]">Áreas:</span><span className="col-span-2 font-medium text-[#153e44]">{form.categories.map(c => CATEGORIES.find(x => x.slug === c)?.name).join(', ')}</span></div>
              </div>
              <div className="flex items-start gap-2 mt-5">
                <Checkbox id="accept" checked={form.accept} onCheckedChange={(v) => set('accept', v)} />
                <label htmlFor="accept" className="text-sm text-[#354c54] cursor-pointer leading-relaxed">
                  Declaro que as informações acima são verdadeiras e concordo com os <a href="#" className="text-[#153e44] font-medium underline">termos de uso</a> e o <a href="#" className="text-[#153e44] font-medium underline">Provimento OAB nº 205/2021</a>.
                </label>
              </div>
            </div>
          )}

          {/* Nav buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#e1e9eb]">
            <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}>
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar
            </Button>
            {step < 4 ? (
              <Button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()} className="bg-[#153e44] hover:bg-[#0c3036]">
                Continuar <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={!canNext() || submitting} className="bg-[#153e44] hover:bg-[#0c3036] text-white font-semibold">
                {submitting ? 'Enviando...' : 'Finalizar cadastro'}
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-[#425a62] mt-6">
          Já tem uma conta? <Link to="/login" className="font-semibold text-[#153e44] hover:text-[#063f4c]">Entrar</Link>
        </p>
      </div>
    </div>
  );
}
